import ParcelSupervision from '../../components/ParcelSupervision';
import type { ParcelSupervisionData } from '../../lib/parcelSupervision';
import React, { useCallback, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CircleMarker, MapContainer, Polygon, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { OPENSTREETMAP_ATTRIBUTION } from '../../config/mapAttribution';
import { useAuth } from '../../contexts/AuthContext';
import { canIssuePhysicalCommands } from '../../auth/cognito';
import ParcelConfirmDialog from '../../components/ParcelConfirmDialog';
import {
  useParcelValveCommand,
  type ParcelCommandOutcome,
} from '../../hooks/useParcelValveCommand';
import { parcelControlAvailability, parcelValveState, parcelMayBePowerSaving, type ParcelValveState } from '../../lib/parcelOperational';
import { commandStatusKind, isBusyStatus } from '../../lib/valveCommandSafety';
import type { ParcelValveBinding } from '../../lib/iotApi';
import SoverdiSiteSelector from '../../soverdi-clone/components/SoverdiSiteSelector';
import { useAgriSafeSoverdiDashboard } from '../../soverdi-clone/hooks/useAgriSafeSoverdiDashboard';
import { orderedSiteNames } from '../../soverdi-clone/adapters/dashboardContext';
import {
  boundsOf,
  buildCgmGeography,
  parcelsForSite,
  pumpsForSite,
  type CgmGeography,
  type LatLngBounds,
} from '../../soverdi-clone/geo/cgmGeography';

const OSM_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

const FitBounds: React.FC<{ bounds: LatLngBounds | null }> = ({ bounds }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!bounds) return;
    map.fitBounds(bounds as [[number, number], [number, number]], { padding: [32, 32], maxZoom: 17 });
  }, [map, bounds]);
  return null;
};

const valveFill = (state: ParcelValveState): string => {
  switch (state) {
    case 'open': return '#22c55e';
    case 'closed': return '#94a3b8';
    case 'stale': return '#f59e0b';
    default: return '#64748b';
  }
};

interface OperationalParcelCardProps {
  supervision?: ParcelSupervisionData;
  binding: ParcelValveBinding;
  displayName?: string;
  state: ParcelValveState;
  powerSavingPossible: boolean;
  canOpen: boolean;
  canClose: boolean;
  statusText: string | null;
  availabilityText: string | null;
  onOpen: () => void;
  onClose: () => void;
}

const OperationalParcelCard: React.FC<OperationalParcelCardProps> = ({
  binding,
  displayName,
  supervision,
  state,
  powerSavingPossible,
  canOpen,
  canClose,
  statusText,
  availabilityText,
  onOpen,
  onClose,
}) => {
  const { t } = useTranslation(['common', 'agrisafeR1']);
  const stateLabel = powerSavingPossible
    ? t('agrisafeR1:parcel.state.powerSavingPossible')
    : state === 'open'
    ? t('agrisafeR1:parcel.state.open')
    : state === 'closed'
      ? t('agrisafeR1:parcel.state.closed')
      : state === 'stale'
        ? t('agrisafeR1:parcel.state.stale')
        : t('agrisafeR1:parcel.state.unknown');

  return (
    <article
      className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4"
      data-testid={`operational-sector-${binding.binding_id}`}
    >
      <div className="flex flex-col items-start gap-2">
        <div className="min-w-0">
          <h3 className="break-words font-semibold text-[var(--text-primary)]">{displayName ?? binding.parcel}</h3>
          <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
            {binding.site} · {t('agrisafeR1:sectors.sectorTitle', { code: binding.sector_code })}
          </p>
        </div>
        <span className="max-w-full rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: `${valveFill(state)}20`, color: valveFill(state) }}>
          {stateLabel}
        </span>
      </div>

      <ParcelSupervision data={supervision} />
      {(statusText || availabilityText) && (
        <div className="mt-3 text-xs text-[var(--text-secondary)]" data-testid={`operational-sector-status-${binding.binding_id}`}>
          {availabilityText ?? statusText}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onOpen}
          disabled={!canOpen}
          className="min-h-[46px] rounded-xl bg-emerald-500 px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[var(--gray-200)] disabled:text-[var(--gray-500)]"
        >
          {t('agrisafeR1:parcel.open')}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={!canClose}
          className="min-h-[46px] rounded-xl bg-red-500 px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[var(--gray-200)] disabled:text-[var(--gray-500)]"
        >
          {t('agrisafeR1:parcel.close')}
        </button>
      </div>
    </article>
  );
};

const CgmSectorsR1: React.FC = () => {
  const { t } = useTranslation(['common', 'agrisafeR1']);
  const { user } = useAuth();
  const {
    siteModels,
    parcelDisplayName,
    topology,
    parcelBindings,
    nodes,
    refreshNodes,
    loading,
    loadError,
    bindingsUnavailable,
  } = useAgriSafeSoverdiDashboard();
  const [selectedSiteName, setSelectedSiteName] = useState('');

  const siteNames = useMemo(() => orderedSiteNames(siteModels), [siteModels]);
  const effectiveSiteName = selectedSiteName || siteNames[0] || '';

  const operationalBindings = useMemo(
    () => parcelBindings.filter(
      (binding) => binding.site === effectiveSiteName && binding.binding_status === 'CONFIRMED',
    ),
    [parcelBindings, effectiveSiteName],
  );
  const operationalSectorCount = useMemo(
    () => operationalBindings.length,
    [operationalBindings],
  );

  const command = useParcelValveCommand({
    role: user?.role,
    nodes,
    refreshNodes,
  });
  const roleAuthorized = canIssuePhysicalCommands(user?.role);
  const now = Date.now();

  const outcomeLabel = useCallback((outcome: ParcelCommandOutcome): string | null => {
    switch (outcome) {
      case 'unassigned': return t('agrisafeR1:sectorDetail.notAssigned');
      case 'semantic_binding_required': return t('agrisafeR1:dashboard.irrigationMappingToConfirm');
      case 'commissioning_incomplete': return t('agrisafeR1:parcel.lockReason.COMMISSIONING_NOT_COMPLETE');
      case 'actuation_not_authorized': return t('agrisafeR1:parcel.lockReason.ACTUATION_NOT_AUTHORIZED');
      case 'wiring_not_confirmed': return t('agrisafeR1:pumping.diagnostic.channelPending');
      case 'conflict': return t('agrisafeR1:parcel.status.conflict');
      case 'forbidden': return t('agrisafeR1:parcel.status.forbidden');
      case 'uncertain': return t('agrisafeR1:parcel.status.uncertain');
      default: return null;
    }
  }, [t]);

  const geography = useMemo<CgmGeography>(
    () => (topology ? buildCgmGeography(topology) : { parcels: [], pumps: [], unmapped: [] }),
    [topology],
  );
  const siteParcels = useMemo(
    () => parcelsForSite(geography, effectiveSiteName),
    [geography, effectiveSiteName],
  );
  const operationalParcelNames = useMemo(
    () => new Set(operationalBindings.map((binding) => binding.parcel)),
    [operationalBindings],
  );
  const visibleParcels = useMemo(
    () => siteParcels.filter((parcel) => operationalParcelNames.has(parcel.canonicalParcel)),
    [siteParcels, operationalParcelNames],
  );
  const sitePumps = useMemo(
    () => pumpsForSite(geography, effectiveSiteName),
    [geography, effectiveSiteName],
  );
  const bounds = useMemo<LatLngBounds | null>(
    () => boundsOf(
      visibleParcels.map((parcel) => parcel.ring),
      sitePumps.map((pump) => pump.position),
    ),
    [visibleParcels, sitePumps],
  );
  const hasAnyGeometry = visibleParcels.length > 0 || sitePumps.length > 0;

  const bindingByParcel = useMemo(
    () => new Map(operationalBindings.map((binding) => [binding.parcel, binding])),
    [operationalBindings],
  );

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>;
  }

  if (loadError && !topology) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="sectors-load-error">
        <p className="text-sm text-[var(--text-secondary)]">{t('common:availabilityUnavailable')}</p>
      </div>
    );
  }

  if (bindingsUnavailable) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="sectors-bindings-unavailable">
        <p className="max-w-xl text-center text-sm text-[var(--text-secondary)]">{t('agrisafeR1:dashboard.dataUnavailable')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6" data-testid="cgm-sectors-r1">
      <SoverdiSiteSelector
        sites={siteNames}
        selected={effectiveSiteName}
        onSelect={setSelectedSiteName}
      />

      <header>
        <h1 className="text-xl sm:text-2xl font-semibold text-[var(--text-primary)]">{t('agrisafeR1:sectors.title')}</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {t('agrisafeR1:sectors.subtitle', {
            sectorCount: operationalSectorCount,
            parcelCount: operationalBindings.length,
          })}
        </p>
      </header>

      <section
        className="relative overflow-hidden rounded-2xl border border-[var(--border-light)] bg-[var(--surface)]"
        data-testid="sectors-map-panel"
      >
        {hasAnyGeometry ? (
          <MapContainer
            bounds={bounds as [[number, number], [number, number]] | undefined}
            scrollWheelZoom={false}
            dragging={false}
            zoomControl={false}
            doubleClickZoom={false}
            touchZoom={false}
            boxZoom={false}
            keyboard={false}
            className="h-[360px] sm:h-[440px] w-full"
          >
            <TileLayer attribution={OPENSTREETMAP_ATTRIBUTION} url={OSM_TILE_URL} />
            <FitBounds bounds={bounds} />
            {visibleParcels.map((parcel) => {
              const binding = bindingByParcel.get(parcel.canonicalParcel);
              if (!binding) return null;
              const node = nodes.find((item) => item.id === binding.controller_node_id);
              const state = parcelValveState(node, binding.channel_id, now);
              return (
                <Polygon
                  key={parcel.kmlName}
                  positions={parcel.ring as [number, number][]}
                  interactive={false}
                  pathOptions={{
                    color: valveFill(state),
                    weight: 2,
                    fillColor: valveFill(state),
                    fillOpacity: 0.5,
                  }}
                />
              );
            })}
            {sitePumps.map((pump) => (
              <CircleMarker
                key={pump.kmlName}
                center={pump.position as [number, number]}
                radius={8}
                interactive={false}
                pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#111827', fillOpacity: 1 }}
              />
            ))}
          </MapContainer>
        ) : (
          <div className="h-[360px] sm:h-[440px] w-full flex flex-col items-center justify-center gap-2 px-6 text-center" data-testid="sectors-map-unavailable">
            <MapPin className="w-6 h-6 text-[var(--text-tertiary)]" />
            <p className="text-sm font-semibold text-[var(--text-primary)]">{t('common:mapUnavailableTitle')}</p>
            <p className="text-xs text-[var(--text-secondary)] max-w-sm">{t('common:mapUnavailableDetail')}</p>
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="cgm-sector-cards">
        <div>
          <h2 className="font-semibold text-[var(--text-primary)]">
            {t('agrisafeR1:sectors.listTitleForSite', { site: effectiveSiteName })}
          </h2>
          <p className="text-xs text-[var(--text-secondary)] mt-1">{t('agrisafeR1:nav.pilotageDesc')}</p>
        </div>

        {operationalBindings.length === 0 ? (
          <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-4 py-8 text-center text-sm text-[var(--text-secondary)]">
            {t('common:noSectors')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {operationalBindings.map((binding) => {
              const node = nodes.find((item) => item.id === binding.controller_node_id);
              const state = parcelValveState(node, binding.channel_id, now);
              const rawStatus = command.statusFor(binding.binding_id);
              const outcome = command.outcomeFor(binding.binding_id);
              const busy = isBusyStatus(rawStatus);
              const authorized = binding.command_authorized && roleAuthorized && outcome === null;
              const { canOpen, canClose } = parcelControlAvailability(state, authorized, busy);
              const statusKind = commandStatusKind(rawStatus);
              const statusText = outcomeLabel(outcome)
                ?? (busy
                  ? rawStatus === 'UNCERTAIN'
                    ? t('agrisafeR1:parcel.status.uncertain')
                    : t('agrisafeR1:parcel.status.busy')
                  : statusKind === 'confirmed'
                    ? t('agrisafeR1:sectorDetail.commandState.confirmed')
                    : statusKind === 'failed'
                      ? t('agrisafeR1:sectorDetail.commandState.failed')
                      : statusKind === 'timeout'
                        ? t('agrisafeR1:parcel.status.timeout')
                        : statusKind === 'uncertain'
                          ? t('agrisafeR1:parcel.status.uncertain')
                          : null);
              const availabilityText = !roleAuthorized
                ? t('agrisafeR1:sectorDetail.block.notAuthorized')
                : busy
                  ? statusText ?? t('agrisafeR1:sectorDetail.block.commandBusy')
                  : null;

              return (
                <OperationalParcelCard
                  key={binding.binding_id}
                  binding={binding}
                  displayName={parcelDisplayName?.(binding)}
                  supervision={node?.parcel_supervision?.[binding.binding_id]}
                  state={state}
                  powerSavingPossible={!loading && !loadError && !bindingsUnavailable
                    && !statusText && outcome === null && parcelMayBePowerSaving(binding, node, state)}
                  canOpen={canOpen}
                  canClose={canClose}
                  statusText={statusText}
                  availabilityText={availabilityText}
                  onOpen={() => command.requestCommand(binding, 'open')}
                  onClose={() => command.requestCommand(binding, 'close')}
                />
              );
            })}
          </div>
        )}
      </section>

      {command.pending && (
        <ParcelConfirmDialog
          pending={command.pending}
          onCancel={command.cancelConfirmation}
          onConfirm={() => { void command.confirmCommand(); }}
        />
      )}
    </div>
  );
};

export default CgmSectorsR1;
